/**
 * Customer-portal user profile: tenant-scoped, no admin roles or project groups.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import adminStyles from "../ui/AdminStyles";
import { StatusBadge } from "../ui/RoleBadges";
import { authedDelete, authedGet, authedPost } from "../api/customerAuthedFetch";
import CustomerRoleHelp from "../components/CustomerRoleHelp";
import InfoTip from "../components/InfoTip";
import {
  CUSTOMER_ROLE_OPTIONS,
  normalizeCustomerRoles,
} from "../customerRoles";

const CUSTOMER_ROLES = CUSTOMER_ROLE_OPTIONS;

const TIPS = {
  lock: "Unlock to edit this profile. Lock again when finished to avoid accidental changes.",
  username: "Sign-in username. Cannot be changed after the account is created.",
  email: "Email for invites, password resets, and notification delivery.",
  name: "Display name shown in the user directory and recipient lists.",
  accountStatus:
    "Enabled users can sign in. Disabled users are blocked until re-enabled.",
  authStatus: "Cognito authentication state for this account.",
  resetPassword:
    "Send a password-reset email and require the user to set a new password on next sign-in.",
  resendInvite:
    "Resend the invitation email. Only available while the account is Unconfirmed.",
  deleteUser: "Permanently remove this user from the organization. This cannot be undone.",
};

const sortCI = (a, b) =>
  String(a).localeCompare(String(b), undefined, { sensitivity: "base" });

export default function CustomerUserProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnLocation = location.state?.from;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(true);
  const [original, setOriginal] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const isDirty = useMemo(() => {
    if (!original || !draft) return false;
    return JSON.stringify(original) !== JSON.stringify(draft);
  }, [original, draft]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await authedGet(
          `/customer/users/${encodeURIComponent(username)}`
        );
        const normalized = {
          username: data.username,
          email: data.email || "",
          name: data.name || "",
          enabled: !!data.enabled,
          status: data.status || data.auth_status || "UNKNOWN",
          customerRoles: normalizeCustomerRoles(
            Array.isArray(data.customerRoles)
              ? data.customerRoles
              : Array.isArray(data.roles)
                ? data.roles
                : []
          ),
        };
        if (!cancelled) {
          setOriginal(normalized);
          setDraft(normalized);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load user");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [username]);

  useEffect(() => {
    document.title = "Users · " + (draft?.name || username);
  }, [draft, username]);

  function update(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  function toggleCustomerRole(roleId) {
    setDraft((prev) => {
      const next = prev.customerRoles.includes(roleId)
        ? prev.customerRoles.filter((r) => r !== roleId)
        : [...prev.customerRoles, roleId];
      return { ...prev, customerRoles: next };
    });
  }

  function handleCancel() {
    if (isDirty && !window.confirm("Discard unsaved changes?")) return;
    setDraft(original);
    setLocked(true);
  }

  async function performSave() {
    setSaving(true);
    setError(null);
    try {
      await authedPost(
        `/customer/users/${encodeURIComponent(draft.username)}/update`,
        {
          email: draft.email || null,
          name: draft.name || null,
          enabled: !!draft.enabled,
          roles: (draft.customerRoles || []).slice().sort(sortCI),
        }
      );
      setOriginal(JSON.parse(JSON.stringify(draft)));
      setLocked(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteUser() {
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await authedDelete(
        `/customer/users/${encodeURIComponent(draft.username)}`
      );
      if (returnLocation) {
        navigate(returnLocation.pathname + (returnLocation.search || ""), {
          replace: true,
          state: { reloadUsers: true },
        });
      } else {
        navigate("/organization/users", { replace: true, state: { reloadUsers: true } });
      }
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setDeleteLoading(false);
    }
  }

  function goBack() {
    if (isDirty && !window.confirm("You have unsaved changes. Leave anyway?")) {
      return;
    }
    if (returnLocation) {
      navigate(returnLocation.pathname + (returnLocation.search || ""));
    } else {
      navigate("/organization/users");
    }
  }

  if (loading) return <p>Loading…</p>;
  if (error && !draft) return <p style={{ color: "red" }}>{error}</p>;
  if (!draft) return null;

  const requiredDeleteText = `DELETE ${draft.username}`;

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>{draft.name || draft.username}</h1>
          <div style={{ color: "#6b7280" }}>{draft.email}</div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {!locked && (
            <>
              <button disabled={!isDirty || saving} onClick={performSave}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={handleCancel}>Cancel</button>
            </>
          )}
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => setLocked((v) => !v)}
              style={{ background: "none", border: "none", fontSize: 22 }}
              title={locked ? "Unlock profile" : "Lock profile"}
            >
              {locked ? "🔒" : "🔓"}
            </button>
            <InfoTip text={TIPS.lock} align="end" />
          </span>
          <button type="button" onClick={goBack}>
            ← Back
          </button>
        </div>
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}

      <section>
        <h3>General Information</h3>
        <table style={adminStyles.table}>
          <tbody>
            <Row tip={TIPS.username} label="Username">
              <code>{draft.username}</code>
            </Row>
            <Row tip={TIPS.email} label="Email">
              <input
                value={draft.email || ""}
                disabled={locked}
                style={{ minWidth: 280 }}
                onChange={(e) => update("email", e.target.value)}
              />
            </Row>
            <Row tip={TIPS.name} label="Name">
              <input
                value={draft.name || ""}
                disabled={locked}
                placeholder="Full name"
                style={{ minWidth: 280 }}
                onChange={(e) => update("name", e.target.value)}
              />
            </Row>
            <Row
              label={
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  Customer Roles
                  <CustomerRoleHelp />
                </span>
              }
            >
              {CUSTOMER_ROLES.map((r) => (
                <label
                  key={r.id}
                  style={{ display: "block", opacity: locked ? 0.6 : 1 }}
                >
                  <input
                    type="checkbox"
                    disabled={locked}
                    checked={draft.customerRoles.includes(r.id)}
                    onChange={() => toggleCustomerRole(r.id)}
                  />{" "}
                  {r.label}
                </label>
              ))}
            </Row>
          </tbody>
        </table>
      </section>

      <section style={{ marginTop: 32 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 24,
            alignItems: "start",
          }}
        >
          <div>
            <h3 style={{ marginBottom: 8 }}>Account Controls</h3>
            <div style={{ border: "1px solid #e5e7eb", padding: 16 }}>
              <table style={adminStyles.table}>
                <tbody>
                  <Row tip={TIPS.accountStatus} label="Account Status">
                    <span
                      style={{
                        padding: "4px 8px",
                        borderRadius: 6,
                        background: draft.enabled ? "#dcfce7" : "#fee2e2",
                        color: draft.enabled ? "#166534" : "#991b1b",
                        fontWeight: 600,
                      }}
                    >
                      {draft.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </Row>
                </tbody>
              </table>
              <div style={{ marginTop: 20 }}>
                <button
                  disabled={locked}
                  onClick={() => update("enabled", !draft.enabled)}
                >
                  {draft.enabled ? "Disable User" : "Enable User"}
                </button>
              </div>
            </div>
          </div>
          <div>
            <h3 style={{ marginBottom: 8 }}>Security</h3>
            <div style={{ border: "1px solid #e5e7eb", padding: 16 }}>
              <table style={adminStyles.table}>
                <tbody>
                  <Row tip={TIPS.authStatus} label="Auth Status">
                    <StatusBadge status={draft.status} />
                  </Row>
                </tbody>
              </table>
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <button
                  disabled={locked}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        "Reset password and force user to change it on next login?"
                      )
                    ) {
                      return;
                    }
                    try {
                      await authedPost(
                        `/customer/users/${encodeURIComponent(draft.username)}/reset-password`,
                        {}
                      );
                      alert("Password reset initiated. User will receive an email.");
                      setDraft((prev) => ({
                        ...prev,
                        status: "FORCE_CHANGE_PASSWORD",
                      }));
                    } catch (e) {
                      alert(e.message || "Reset failed");
                    }
                  }}
                >
                  Reset Password
                </button>
                <InfoTip text={TIPS.resetPassword} />
                <button
                  disabled={locked || draft.status !== "UNCONFIRMED"}
                  onClick={async () => {
                    try {
                      await authedPost(
                        `/customer/users/${encodeURIComponent(draft.username)}/resend-invite`,
                        {}
                      );
                      alert("Invitation email resent.");
                    } catch (e) {
                      alert(e.message || "Resend failed");
                    }
                  }}
                >
                  Resend Invite
                </button>
                <InfoTip text={TIPS.resendInvite} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ marginTop: 40 }}>
        <h3 style={{ color: "#991b1b" }}>Danger Zone</h3>
        <div
          style={{
            border: "1px solid #fecaca",
            padding: 16,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <button
            disabled={locked}
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              background: locked ? "#fca5a5" : "#dc2626",
              color: "white",
              fontWeight: 700,
            }}
          >
            Delete User
          </button>
          <InfoTip text={TIPS.deleteUser} />
        </div>
      </section>

      {showDeleteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: "min(520px, 95vw)",
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              border: "2px solid #dc2626",
            }}
          >
            <h3 style={{ marginTop: 0, color: "#991b1b" }}>
              Permanently delete user
            </h3>
            <p>This action cannot be undone.</p>
            <p>
              Type <code>{requiredDeleteText}</code> to confirm:
            </p>
            <input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type confirmation text"
              style={{
                width: "100%",
                padding: 8,
                border: "1px solid #dc2626",
                borderRadius: 6,
                marginBottom: 8,
              }}
              disabled={deleteLoading}
            />
            {deleteError && <p style={{ color: "#991b1b" }}>{deleteError}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmText("");
                  setDeleteError(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                disabled={
                  deleteConfirmText !== requiredDeleteText || deleteLoading
                }
                onClick={handleDeleteUser}
                style={{ background: "#dc2626", color: "#fff" }}
              >
                {deleteLoading ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, tip, children }) {
  return (
    <tr>
      <td style={{ ...adminStyles.td, fontWeight: 600, width: 220 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {label}
          {tip ? <InfoTip text={tip} /> : null}
        </span>
      </td>
      <td style={adminStyles.td}>{children}</td>
    </tr>
  );
}
